import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button, IconButton } from '@mui/material';

import login from 'api/requests/login';
import Form, { FormInput } from 'components/common/Form';
import useAuth, { AuthLoginSchemaType } from 'store/auth';
import { loginSchema } from 'configs/validationSchemes';
import useYupValidationResolver from 'hooks/useYupValidationResolver';
import { translate, TranslationKey } from 'utils/translate';
import Modal from 'components/common/Modal';
import DotIcon from 'components/common/icons/Dot';
import useScreen from 'hooks/useScreen';
import BiometricLoginPopup from 'utils/messaging/biometric/BiometricLoginPopup';
import useBiometric from 'utils/messaging/biometric/useBiometric';
import Fingerprint from 'components/common/icons/FingerPrint';

import sx from './styles';

export interface ISignInFormModalProps {
  title: TranslationKey;
  onClose: () => void;
  openForgotPasswordModal: () => void;
}

const SignInFormModal: FC<ISignInFormModalProps> = ({ title, onClose, openForgotPasswordModal }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [authState, authActions] = useAuth();
  const { isMobile } = useScreen();
  const [isBiometricEnabledPopupOpen, setIsBiometricEnabledPopupOpen] = useState(false);

  const { biometricInfo, enableBiometric, authenticate } = useBiometric();

  const resolver = useYupValidationResolver<{ email: string; password: string }>(loginSchema);
  const { control, handleSubmit, getValues } = useForm<AuthLoginSchemaType>({
    resolver,
  });

  const handleLoginSubmit = async (data: AuthLoginSchemaType): Promise<void> => {
    setLoading(true);
    if (biometricInfo.success) {
      await login(data);
      setIsBiometricEnabledPopupOpen(true);
      setLoading(false);
      return;
    }

    await authActions.login(data);
    navigate('/dashboard');
    setLoading(false);

    onClose();
  };

  const onSave = async (): Promise<void> => {
    handleSubmit(handleLoginSubmit)();
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> | undefined = (e) => {
    if (e.key === 'Enter') {
      onSave();
    }
  };

  return (
    <>
      <Modal
        title={title}
        fullScreen={isMobile}
        icon={<DotIcon fill="red.scale.300" />}
        saveText="Sign In"
        isLoading={loading || authState?.isLoading}
        onSave={onSave}
        onClose={onClose}
        maxWidth={isMobile ? 600 : 400}
      >
        <Form sx={sx.form} spacing={2.5} onSubmit={handleSubmit(handleLoginSubmit)}>
          <FormInput<AuthLoginSchemaType>
            onKeyDown={onKeyDown}
            required
            label="Username or Email"
            control={control}
            name="email"
          />
          <FormInput<AuthLoginSchemaType>
            onKeyDown={onKeyDown}
            required
            label="Password"
            control={control}
            name="password"
            type="password"
          />
        </Form>
        {biometricInfo.success && (
          <IconButton
            color="primary"
            onClick={(): void => {
              biometricInfo.enabled ? authenticate() : setIsBiometricEnabledPopupOpen(true);
            }}
          >
            <Fingerprint />
          </IconButton>
        )}
        <Button onClick={openForgotPasswordModal} sx={{ color: 'custom.common.black', display: 'block', mx: 'auto' }}>
          {translate('Forgot Password')}
        </Button>
      </Modal>
      {isBiometricEnabledPopupOpen && (
        <BiometricLoginPopup
          onClose={(): void => setIsBiometricEnabledPopupOpen(false)}
          onSave={(): void => {
            enableBiometric(getValues());
          }}
        />
      )}
    </>
  );
};

export default SignInFormModal;
